import { z } from 'zod';
import { whatsAppSettingsSchema } from '../whatsapp/schemas.js';
/** docs section 5 "Administrator Control" — every namespace's shape is
 * declared in code (here) and validated on write; the app never reads an
 * unknown key (docs/architecture/03 § tenant_settings). */
export const SETTINGS_NAMESPACES = [
    'general',
    'branding',
    'security',
    'email',
    'notifications',
    'data_retention',
    'lead_dedupe',
    'pipeline_config',
    'ai_qualification',
    'whatsapp',
];
export const generalSettingsSchema = z.object({
    companyName: z.string().trim().min(1).max(150),
    defaultCurrency: z.string().length(3),
    fiscalYearStartMonth: z.number().int().min(1).max(12).default(1),
    timeZone: z.string().default('America/Toronto'),
});
export const brandingSettingsSchema = z.object({
    productName: z.string().trim().min(1).max(60).default('Orion'),
    /** The sidebar's workspace subtitle ("OES Workspace") — previously
     * hardcoded literal text in AppShell with nothing behind it
     * (design/orion-crm-design/rendered-html/AdminBranding.html's "Workspace
     * name" field). */
    workspaceName: z.string().trim().min(1).max(100).default('Workspace'),
    accentColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .default('#2E47D6'),
    loginHeadline: z.string().trim().max(200).optional(),
    loginSubtext: z.string().trim().max(160).optional(),
    supportEmail: z.string().trim().toLowerCase().email().optional(),
    showPoweredBy: z.boolean().default(true),
    // Set only by BrandingService's logo/favicon upload/remove endpoints,
    // never through the generic settings form — the storage key is
    // meaningless to an admin and the mime type must always match the bytes
    // on disk. Light = used on light surfaces (login page); dark = used on
    // the dark sidebar.
    logoStorageKey: z.string().nullable().default(null),
    logoMimeType: z.string().nullable().default(null),
    logoDarkStorageKey: z.string().nullable().default(null),
    logoDarkMimeType: z.string().nullable().default(null),
    faviconStorageKey: z.string().nullable().default(null),
    faviconMimeType: z.string().nullable().default(null),
});
/** Which membership tiers must have a confirmed MFA factor. 'everyone'
 * subsumes 'admins'/'managers' — kept as a separate flag rather than a
 * derived one so the UI's 3 independent checkboxes (design/orion-crm-design/
 * rendered-html/AdminSecurity.html) map 1:1 onto stored state. */
export const MFA_REQUIRED_TIERS = ['admins', 'managers', 'everyone'];
export const securitySettingsSchema = z.object({
    /** Tenant-wide kill switch — Admin ▸ Authentication & SSO's "Email +
     * password" toggle (design/orion-crm-design/rendered-html/AdminSSO.html).
     * Enforced in `AuthService#login`, ahead of the lockout/allowlist checks. */
    passwordSignInEnabled: z.boolean().default(true),
    passwordMinLength: z.number().int().min(8).max(128).default(12),
    breachedPasswordCheck: z.boolean().default(true),
    mfaRequiredFor: z.array(z.enum(MFA_REQUIRED_TIERS)).default(['admins']),
    sessionIdleTimeoutMinutes: z.number().int().min(5).max(480).default(30),
    sessionAbsoluteLifetimeHours: z.number().int().min(1).max(168).default(12),
    /** "Remember me" session ceiling — previously only a global env var
     * (SESSION_REMEMBER_MAX_DAYS); now the per-tenant source of truth that
     * `AuthService#issueSession` actually reads. */
    rememberMeMaxDays: z.number().int().min(1).max(365).default(30),
    lockoutMaxAttempts: z.number().int().min(3).max(20).default(5),
    lockoutWindowMinutes: z.number().int().min(5).max(120).default(15),
    /** One IPv4/IPv6 address or IPv4 CIDR range per entry — empty means no
     * restriction. Enforced in `AuthService#login` before credentials are even
     * checked. */
    ipAllowlist: z.array(z.string().trim().min(1)).default([]),
});
export const emailSettingsSchema = z.object({
    provider: z.enum(['brevo', 'smtp', 'msgraph', 'gmail', 'postmark']).default('brevo'),
    fromName: z.string().trim().min(1).max(100),
    fromAddress: z.string().trim().toLowerCase().email(),
    replyTo: z.string().trim().toLowerCase().email().optional(),
});
/** One row of Admin ▸ Email & notifications' rule table
 * (design/orion-crm-design/rendered-html/AdminEmail.html) — `inApp`/`email`
 * are real gates every sender checks before creating a Notification row or
 * calling the email port; `optOutAllowed` is a stored workspace policy with
 * no per-user preference screen to consume it yet (disclosed in
 * NotificationSettingsPage/EmailSettingsPage). */
const notificationEventSchema = z.object({
    inApp: z.boolean(),
    email: z.boolean(),
    optOutAllowed: z.boolean(),
});
export const NOTIFICATION_EVENT_KEYS = [
    'leadCreated',
    'leadAssigned',
    'mention',
    'taskReminder',
    'dealWonLost',
    'importCompleted',
    'teamMemberAdded',
    'teamMemberRemoved',
];
export const notificationSettingsSchema = z.object({
    /** Fires on every new lead, to the tenant's admins (`admin.settings.manage`
     * holders) — not a per-user preference, so `optOutAllowed: false` like
     * `teamMemberAdded`/`importCompleted`. `inApp` also gates the live
     * celebration popup (`RealtimeService`), not just the bell entry. */
    leadCreated: notificationEventSchema.default({ inApp: true, email: true, optOutAllowed: false }),
    leadAssigned: notificationEventSchema.default({ inApp: true, email: true, optOutAllowed: true }),
    mention: notificationEventSchema.default({ inApp: true, email: true, optOutAllowed: true }),
    taskReminder: notificationEventSchema.default({ inApp: true, email: true, optOutAllowed: true }),
    dealWonLost: notificationEventSchema.default({ inApp: true, email: false, optOutAllowed: true }),
    importCompleted: notificationEventSchema.default({ inApp: true, email: true, optOutAllowed: false }),
    teamMemberAdded: notificationEventSchema.default({ inApp: true, email: true, optOutAllowed: false }),
    teamMemberRemoved: notificationEventSchema.default({ inApp: true, email: true, optOutAllowed: false }),
    /** Tracking card — neither is wired into the send pipeline yet (no sent
     * email is logged to a record's timeline, no open-tracking pixel is
     * injected); stored for real so the toggle isn't lost the moment it's
     * built, disclosed as inert until then. */
    logSentEmailsToTimeline: z.boolean().default(true),
    openTracking: z.boolean().default(false),
});
export const dataRetentionSettingsSchema = z.object({
    archivedRecordPurgeDays: z.number().int().min(30).max(3650).default(90),
    auditLogRetentionDays: z.number().int().min(90).max(3650).default(365),
});
/** Admin ▸ Leads' "Duplicate detection" card (design/orion-crm-design/
 * rendered-html/AdminLeadConfig.html) — read by `DedupeService` on every
 * lead create, not just displayed. `onMatchAction: 'merge'` is accepted and
 * stored but treated the same as `'warn'`: silently auto-merging two
 * records on a fuzzy match is exactly the kind of destructive action this
 * app never takes without a human choosing it (docs/architecture/10 §
 * R11) — `LeadsService#create` never merges on its own. */
export const leadDedupeSettingsSchema = z.object({
    matchOnEmail: z.boolean().default(true),
    matchOnPhone: z.boolean().default(true),
    matchOnNameCompany: z.boolean().default(false),
    nameCompanyThreshold: z.number().min(0.5).max(1).default(0.85),
    onMatchAction: z.enum(['warn', 'block', 'merge']).default('warn'),
});
/** Admin ▸ Pipelines & stages' "Deal settings" card (design/orion-crm-design/
 * rendered-html/AdminPipeline.html). `requireLossReasonOnLost` and
 * `weightedPipelineEnabled` are both real gates — `DealsService#moveStage`
 * and `DashboardService#get` read them, not just display them.
 * `multiCurrencyEnabled`/`trackedCurrencies` are stored for real (each
 * pipeline's own `currency` is already the base), but nothing downstream
 * reads them yet: the deal-create form still takes a free-text currency and
 * nothing converts a non-base-currency deal for a rolled-up total — a real
 * FX-rate feed and a scheduled refresh are a bigger feature than this
 * settings screen, disclosed rather than faked. */
export const pipelineConfigSettingsSchema = z.object({
    requireLossReasonOnLost: z.boolean().default(true),
    weightedPipelineEnabled: z.boolean().default(true),
    multiCurrencyEnabled: z.boolean().default(false),
    trackedCurrencies: z.array(z.string().length(3)).default(['USD']),
});
/** Admin ▸ AI lead qualification. `enabled: false` (the default) means
 * `AiQualificationService` is never called — Inbox Pulse keeps today's
 * behavior (every real reply from an unrecognized sender becomes a Lead)
 * until an admin turns this on, same "off until configured" convention
 * as every other integration this session. `autoCreateThreshold`/
 * `reviewThreshold` are the two cut lines from the spec (>= auto-create →
 * create/update the lead automatically; between review and auto-create →
 * the "AI Lead Review" queue; below review → not a lead at all) — kept as
 * one pair of numbers here rather than hardcoded anywhere a decision is
 * made, and validated so review can never be above auto-create. No
 * provider/model field here — classification now goes through a
 * free-then-paid fallback chain (Gemini, then OpenAI, then Anthropic),
 * each leg's own API key and model controlled by its own env var
 * (GEMINI_API_KEY/GEMINI_MODEL, OPENAI_API_KEY/OPENAI_MODEL,
 * ANTHROPIC_API_KEY/ANTHROPIC_MODEL, all Doppler) — a single tenant-level
 * "model" setting stopped making sense once more than one provider could
 * answer a given request. Never a secret stored here either way — this is
 * tenant-visible config. */
export const aiQualificationSettingsSchema = z
    .object({
    enabled: z.boolean().default(false),
    autoCreateThreshold: z.number().min(0).max(1).default(0.8),
    reviewThreshold: z.number().min(0).max(1).default(0.5),
    defaultPriority: z.enum(['hot', 'warm', 'cold', 'none']).default('warm'),
    enableSummary: z.boolean().default(true),
    enableAutoClassification: z.boolean().default(true),
})
    .refine((v) => v.reviewThreshold <= v.autoCreateThreshold, {
    message: 'Review threshold must be at or below the auto-create threshold.',
    path: ['reviewThreshold'],
});
/** Who last saved any key in a namespace, and when — the most recently
 * written `tenant_settings` row for that namespace (docs screen "Admin —
 * security", "Last changed Sep 21 by Priya Nair"). */
export const settingsMetaResponseSchema = z.object({
    updatedAt: z.string().datetime().nullable(),
    updatedByName: z.string().nullable(),
});
export const SETTINGS_SCHEMAS = {
    general: generalSettingsSchema,
    branding: brandingSettingsSchema,
    security: securitySettingsSchema,
    email: emailSettingsSchema,
    notifications: notificationSettingsSchema,
    data_retention: dataRetentionSettingsSchema,
    lead_dedupe: leadDedupeSettingsSchema,
    pipeline_config: pipelineConfigSettingsSchema,
    ai_qualification: aiQualificationSettingsSchema,
    whatsapp: whatsAppSettingsSchema,
};
//# sourceMappingURL=settings.js.map